import {
    PublicServerHistoryResponseSchema,
    type PublicServerHistoryMetric,
    type PublicServerHistoryResolution,
    type PublicServerHistoryResponse,
} from "@logscord/public-server";
import {
    computed,
    defineComponent,
    onMounted,
    onUnmounted,
    ref,
    type PropType,
} from "vue";
import { formatDirectoryCount } from "./serverDirectoryShared.js";

const HourlyWindowMs = 7 * 24 * 60 * 60 * 1_000;
const DailyWindowMs = 90 * 24 * 60 * 60 * 1_000;

async function fetchHistory(
    guildId: string,
    metric: PublicServerHistoryMetric,
    resolution: PublicServerHistoryResolution,
    to: Date,
    signal: AbortSignal,
): Promise<PublicServerHistoryResponse> {
    const windowMs = resolution === "hour" ? HourlyWindowMs : DailyWindowMs;
    const parameters = new URLSearchParams({
        metric,
        resolution,
        from : new Date(to.getTime() - windowMs).toISOString(),
        to   : to.toISOString(),
    });
    const response = await fetch(
        `/api/public/servers/${encodeURIComponent(guildId)}/history?${parameters.toString()}`,
        {
            signal,
            cache   : "no-store",
            headers : {
                "accept": "application/json",
            },
        },
    );

    if (!response.ok)
        throw new Error(`Failed to load public server history: ${response.status}`);

    return PublicServerHistoryResponseSchema.parse(await response.json());
}

export default defineComponent({
    props: {
        guildId: {
            type     : String,
            required : true,
        },
        locale: {
            type     : String,
            required : true,
        },
        translate: {
            type     : Function as PropType<(key: string) => string>,
            required : true,
        },
    },

    setup(props) {
        const metric = ref<PublicServerHistoryMetric>("voice");
        const resolution = ref<PublicServerHistoryResolution>("hour");
        const periodEnd = ref(new Date());
        const history = ref<PublicServerHistoryResponse | null>(null);
        const loading = ref(true);
        const failed = ref(false);
        let controller: AbortController | null = null;

        const chart = computed(() => {
            const points = history.value?.points ?? [];

            if (points.length === 0)
                return null;

            const values = points.map((point) => point.value);
            const minimum = Math.min(...values);
            const maximum = Math.max(...values);
            const span = Math.max(1, maximum - minimum);
            const coordinates = points.map((point, index) => ({
                x: points.length === 1 ? 300 : index / (points.length - 1) * 600,
                y: 165 - ((point.value - minimum) / span) * 145,
            }));

            return {
                path: coordinates
                    .map((point, index) => `${index === 0 ? "M" : "L"} ${point.x.toFixed(2)} ${point.y.toFixed(2)}`)
                    .join(" "),
                minimum,
                maximum,
            };
        });

        async function load(): Promise<void> {
            controller?.abort();
            controller = new AbortController();
            loading.value = true;

            try {
                history.value = await fetchHistory(
                    props.guildId,
                    metric.value,
                    resolution.value,
                    periodEnd.value,
                    controller.signal,
                );
                failed.value = false;

            } catch (error) {
                if (!(error instanceof DOMException && error.name === "AbortError"))
                    failed.value = true;

            } finally {
                loading.value = false;
            }
        }

        function movePeriod(direction: -1 | 1): void {
            const windowMs = resolution.value === "hour" ? HourlyWindowMs : DailyWindowMs;
            const nextEnd = new Date(periodEnd.value.getTime() + direction * windowMs);

            periodEnd.value = nextEnd > new Date() ? new Date() : nextEnd;
            void load();
        }

        function resetPeriod(): void {
            periodEnd.value = new Date();
            void load();
        }

        onMounted(() => {
            void load();
        });
        onUnmounted(() => controller?.abort());

        return () => (
            <div class="server-profile-panel server-history">
                <header class="server-history-header">
                    <div>
                        <span class="landing-kicker">{props.translate("server.history.kicker")}</span>
                        <h2>{props.translate("server.history.title")}</h2>
                        <p>{props.translate("server.history.description")}</p>
                    </div>

                    <div class="server-history-controls">
                        <label>
                            <span>{props.translate("server.history.metric")}</span>
                            <select
                                value={metric.value}
                                onChange={(event) => {
                                    metric.value = (event.target as HTMLSelectElement).value as PublicServerHistoryMetric;
                                    void load();
                                }}
                            >
                                <option value="voice">{props.translate("servers.metric.voice")}</option>
                                <option value="members">{props.translate("servers.metric.members")}</option>
                                <option value="online">{props.translate("servers.metric.online")}</option>
                            </select>
                        </label>
                        <label>
                            <span>{props.translate("server.history.resolution")}</span>
                            <select
                                value={resolution.value}
                                onChange={(event) => {
                                    resolution.value = (event.target as HTMLSelectElement).value as PublicServerHistoryResolution;
                                    periodEnd.value = new Date();
                                    void load();
                                }}
                            >
                                <option value="hour">{props.translate("server.history.hour")}</option>
                                <option value="day">{props.translate("server.history.day")}</option>
                            </select>
                        </label>
                    </div>
                </header>

                <div class="server-history-navigation">
                    <button type="button" onClick={() => movePeriod(-1)}>
                        <i class="material-symbols-outlined">chevron_left</i>
                        {props.translate("server.history.previous")}
                    </button>
                    <button type="button" onClick={resetPeriod}>
                        {props.translate("server.history.today")}
                    </button>
                    <button
                        type="button"
                        disabled={periodEnd.value.getTime() >= Date.now() - 60_000}
                        onClick={() => movePeriod(1)}
                    >
                        {props.translate("server.history.next")}
                        <i class="material-symbols-outlined">chevron_right</i>
                    </button>
                </div>

                {loading.value && <div class="server-history-state">{props.translate("server.history.loading")}</div>}
                {failed.value && <div class="server-history-state">{props.translate("server.history.error")}</div>}
                {!loading.value && !failed.value && !chart.value && (
                    <div class="server-history-state">{props.translate("server.history.empty")}</div>
                )}

                {!loading.value && !failed.value && chart.value && history.value && (
                    <div class="server-history-visual">
                        <div class="server-history-scale">
                            <span>{formatDirectoryCount(chart.value.maximum, props.locale)}</span>
                            <span>{formatDirectoryCount(chart.value.minimum, props.locale)}</span>
                        </div>
                        <svg
                            viewBox="0 0 600 180"
                            role="img"
                            aria-label={props.translate("server.history.chart_label")}
                            preserveAspectRatio="none"
                        >
                            <path class="server-history-area" d={`${chart.value.path} L 600 180 L 0 180 Z`} />
                            <path class="server-history-line" d={chart.value.path} />
                        </svg>
                        <div class="server-history-period">
                            <time>{new Intl.DateTimeFormat(props.locale, { dateStyle: "medium" }).format(new Date(history.value.period.from))}</time>
                            <time>{new Intl.DateTimeFormat(props.locale, { dateStyle: "medium" }).format(new Date(history.value.period.to))}</time>
                        </div>
                    </div>
                )}
            </div>
        );
    },
});
